import { useState } from "react"
import { Check, Maximize2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogTitle } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

// One pickable sample in a Post a Project gallery. The whole image always shows
// (contained on a card, never cropped), in a box shaped like the sample: book
// covers are tall, so `shape="book"` gives a 2:3 box. The label sits under the
// box so nothing covers the image, and Enlarge opens the sample full size with
// the same pick toggle.
export function SampleTile({ label, imageSrc, alt, shape, on, disabled, onToggle, placeholder }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="relative">
        <button
          type="button"
          data-gallery-tile
          disabled={disabled}
          onClick={onToggle}
          aria-pressed={on}
          aria-label={label}
          className={cn(
            "relative block w-full overflow-hidden rounded-xl border bg-card transition-colors",
            shape === "book" ? "aspect-[2/3]" : "aspect-square",
            on ? "border-primary ring-2 ring-primary/20" : "border-line-2 hover:border-ink-3",
            disabled && "cursor-not-allowed opacity-40"
          )}
        >
          {imageSrc ? (
            <img src={imageSrc} alt={alt} loading="lazy" className="size-full object-contain p-1.5" />
          ) : (
            placeholder
          )}
          {on && (
            <span className="absolute left-1.5 top-1.5 flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <Check className="size-3" strokeWidth={3} />
            </span>
          )}
        </button>
        {imageSrc && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label={`Enlarge ${label}`}
            className="absolute right-1.5 top-1.5 flex size-7 items-center justify-center rounded-full border border-line-2 bg-card/90 text-muted-foreground shadow-sm transition-colors hover:text-foreground"
          >
            <Maximize2 className="size-3.5" strokeWidth={2.2} />
          </button>
        )}
      </div>
      <span className={cn("truncate text-[11.5px] font-semibold", on ? "text-foreground" : "text-muted-foreground")}>
        {label}
      </span>

      {imageSrc && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-xl" aria-describedby={undefined}>
            <DialogTitle>{label}</DialogTitle>
            <img src={imageSrc} alt={alt} className="mx-auto max-h-[60dvh] w-auto max-w-full object-contain" />
            <DialogFooter>
              <Button
                type="button"
                variant={on ? "outline" : "default"}
                disabled={disabled}
                onClick={() => {
                  onToggle()
                  setOpen(false)
                }}
              >
                {on ? "Remove from picks" : "Pick this one"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
